import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import {
  compressDataBuffer,
  decompressData,
  decompressDataBuffer,
} from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import {
  getSceneVersion,
  isInitializedImageElement,
} from "@excalidraw/element";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  deleteField,
  getDoc,
  runTransaction,
  Bytes,
} from "firebase/firestore";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { FILE_CACHE_MAX_AGE_SEC } from "../app_constants";

import {
  decodeSceneElementsFromStorage,
  encodeSceneElementsForStorage,
  FIREBASE_SCENE_ENCODING,
  isFirebaseSceneEncoding,
} from "./sceneCodec";

import { getSyncableElements } from ".";

import type { FirebaseSceneEncoding } from "./sceneCodec";
import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

// private
// -----------------------------------------------------------------------------

let FIREBASE_CONFIG: Record<string, any>;
try {
  FIREBASE_CONFIG = JSON.parse(import.meta.env.VITE_APP_FIREBASE_CONFIG);
} catch (error: any) {
  console.warn(
    `Error JSON parsing firebase config. Supplied value: ${
      import.meta.env.VITE_APP_FIREBASE_CONFIG
    }`,
  );
  FIREBASE_CONFIG = {};
}

let firebaseApp: ReturnType<typeof initializeApp> | null = null;
let firestore: ReturnType<typeof getFirestore> | null = null;

const _initializeFirebase = () => {
  if (!firebaseApp) {
    firebaseApp = initializeApp(FIREBASE_CONFIG);
  }
  return firebaseApp;
};

const _getFirestore = () => {
  if (!firestore) {
    firestore = getFirestore(_initializeFirebase());
  }
  return firestore;
};

const FILE_STORAGE_URL = (
  import.meta.env.VITE_APP_FILE_STORAGE_URL || "/api/files"
).replace(/\/$/, "");
const FILE_MANIFEST_URL = FILE_STORAGE_URL.endsWith("/api/files")
  ? `${FILE_STORAGE_URL.slice(0, -"/files".length)}/file-manifests`
  : "/api/file-manifests";
const SCENE_STORAGE_URL = FILE_STORAGE_URL.endsWith("/api/files")
  ? `${FILE_STORAGE_URL.slice(0, -"/files".length)}/scenes`
  : "/api/scenes";

const getStorageFileUrl = (path: string) => {
  const encodedPath = path
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");

  return `${FILE_STORAGE_URL}/${encodedPath}`;
};

type StorageBody = Blob | Uint8Array | ArrayBuffer;

export const saveFileToStorage = async ({
  path,
  body,
  contentType = "application/octet-stream",
  customMetadata,
}: {
  path: string;
  body: StorageBody;
  contentType?: string;
  customMetadata?: Record<string, string>;
}) => {
  const headers = new Headers({
    "cache-control": `public, max-age=${FILE_CACHE_MAX_AGE_SEC}`,
    "content-type": contentType,
  });

  if (customMetadata) {
    headers.set("x-r2-metadata", JSON.stringify(customMetadata));
  }

  const response = await fetch(getStorageFileUrl(path), {
    method: "PUT",
    headers,
    body: body as unknown as BodyInit,
  });

  if (!response.ok) {
    throw new Error(`File upload failed with status ${response.status}`);
  }
};

type FirebaseStoredScene = {
  sceneVersion: number;
  iv: Bytes;
  /** Present on legacy documents that store the encrypted scene in Firestore. */
  ciphertext?: Bytes;
  /** Present on new documents whose encrypted scene is stored in R2. */
  sceneObjectId?: string;
  /** One prior object is retained so a reader with stale metadata can finish. */
  previousSceneObjectId?: string;
  ciphertextSize?: number;
  /** Missing on legacy room documents, whose plaintext is uncompressed JSON. */
  compression?: "pako@1";
  /** Missing on documents that store normal Excalidraw element JSON. */
  encoding?: FirebaseSceneEncoding;
};

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(encodeSceneElementsForStorage(elements));
  const encoded = new TextEncoder().encode(json);
  // A free-draw stroke contains many coordinate pairs. Compressing the scene
  // before encryption keeps handwriting-heavy rooms well below Firestore's
  // per-document limit. Encrypted bytes cannot be compressed effectively.
  const compressed = compressDataBuffer(encoded);
  const { encryptedBuffer, iv } = await encryptData(key, compressed);

  return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
  data: FirebaseStoredScene,
  roomKey: string,
  roomId: string,
  ownerWindow: Window & typeof globalThis,
): Promise<readonly ExcalidrawElement[]> => {
  let ciphertext: Uint8Array<ArrayBuffer>;
  if (data.ciphertext) {
    ciphertext = data.ciphertext.toUint8Array() as Uint8Array<ArrayBuffer>;
  } else if (data.sceneObjectId) {
    const response = await ownerWindow.fetch(
      `${SCENE_STORAGE_URL}/${encodeURIComponent(roomId)}/${encodeURIComponent(
        data.sceneObjectId,
      )}`,
      { cache: "no-store" },
    );
    if (!response.ok) {
      throw new Error(`Scene download failed with status ${response.status}`);
    }
    ciphertext = new Uint8Array(await response.arrayBuffer());
  } else {
    throw new Error("Stored scene has no ciphertext");
  }
  const iv = data.iv.toUint8Array() as Uint8Array<ArrayBuffer>;

  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedBytes = data.compression
    ? decompressDataBuffer(decrypted)
    : new Uint8Array(decrypted);
  const decodedData = new TextDecoder("utf-8").decode(decodedBytes);
  const storedElements = JSON.parse(decodedData);
  return isFirebaseSceneEncoding(data.encoding)
    ? decodeSceneElementsFromStorage(storedElements, data.encoding)
    : storedElements;
};

class FirebaseSceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return FirebaseSceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    FirebaseSceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return FirebaseSceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

export const saveFilesToStorage = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        await saveFileToStorage({
          path: `${prefix}/${id}`,
          body: buffer,
        });
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

const getRoomStorageToken = async ({
  roomKey,
  namespace,
  ownerWindow,
}: {
  roomKey: string;
  namespace: "file-manifest" | "scene";
  ownerWindow: Window & typeof globalThis;
}) => {
  const tokenData = new ownerWindow.TextEncoder().encode(
    `excalimath-${namespace}:${roomKey}`,
  );
  return bytesToHex(
    new Uint8Array(
      await ownerWindow.crypto.subtle.digest("SHA-256", tokenData),
    ),
  );
};

export const saveRoomFileManifest = async ({
  roomId,
  roomKey,
  elements,
  ownerWindow,
}: {
  roomId: string;
  roomKey: string;
  elements: readonly ExcalidrawElement[];
  ownerWindow: Window & typeof globalThis;
}) => {
  const token = await getRoomStorageToken({
    roomKey,
    namespace: "file-manifest",
    ownerWindow,
  });
  const referencedFileIds = [
    ...new Set(
      elements
        .filter(isInitializedImageElement)
        .filter((element) => !element.isDeleted)
        .map((element) => element.fileId),
    ),
  ];
  const response = await ownerWindow.fetch(
    `${FILE_MANIFEST_URL}/${encodeURIComponent(roomId)}`,
    {
      method: "PUT",
      headers: {
        "content-type": "application/json",
        "x-r2-gc-token": token,
      },
      body: JSON.stringify({
        sceneVersion: getSceneVersion(elements),
        referencedFileIds,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `File manifest update failed with status ${response.status}`,
    );
  }
};

const createFirebaseSceneDocument = async (
  elements: readonly SyncableExcalidrawElement[],
  roomKey: string,
  roomId: string,
  ownerWindow: Window & typeof globalThis,
) => {
  const sceneVersion = getSceneVersion(elements);
  const { ciphertext, iv } = await encryptElements(roomKey, elements);
  const sceneObjectId = `${sceneVersion}-${bytesToHex(iv)}`;
  const token = await getRoomStorageToken({
    roomKey,
    namespace: "scene",
    ownerWindow,
  });
  const response = await ownerWindow.fetch(
    `${SCENE_STORAGE_URL}/${encodeURIComponent(roomId)}/${encodeURIComponent(
      sceneObjectId,
    )}`,
    {
      method: "PUT",
      headers: {
        "content-type": "application/octet-stream",
        "x-r2-scene-token": token,
      },
      body: ciphertext,
    },
  );
  if (!response.ok) {
    throw new Error(`Scene upload failed with status ${response.status}`);
  }

  return {
    sceneVersion,
    sceneObjectId,
    ciphertextSize: ciphertext.byteLength,
    iv: Bytes.fromUint8Array(iv),
    compression: "pako@1",
    encoding: FIREBASE_SCENE_ENCODING,
  } as FirebaseStoredScene;
};

const deleteSceneFromStorage = async ({
  roomId,
  roomKey,
  sceneObjectId,
  ownerWindow,
}: {
  roomId: string;
  roomKey: string;
  sceneObjectId: string;
  ownerWindow: Window & typeof globalThis;
}) => {
  const token = await getRoomStorageToken({
    roomKey,
    namespace: "scene",
    ownerWindow,
  });
  const response = await ownerWindow.fetch(
    `${SCENE_STORAGE_URL}/${encodeURIComponent(roomId)}/${encodeURIComponent(
      sceneObjectId,
    )}`,
    {
      method: "DELETE",
      headers: { "x-r2-scene-token": token },
    },
  );
  if (!response.ok) {
    throw new Error(`Scene cleanup failed with status ${response.status}`);
  }
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
  ownerWindow: Window & typeof globalThis,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // bail if no room exists as there's nothing we can do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  const firestore = _getFirestore();
  const docRef = doc(firestore, "scenes", roomId);
  const uploadedSceneObjectIds = new Set<string>();
  const obsoleteSceneObjectIds = new Set<string>();

  const { storedScene, storedElements } = await runTransaction(
    firestore,
    async (transaction) => {
      const snapshot = await transaction.get(docRef);

      if (!snapshot.exists()) {
        const storedScene = await createFirebaseSceneDocument(
          elements,
          roomKey,
          roomId,
          ownerWindow,
        );
        uploadedSceneObjectIds.add(storedScene.sceneObjectId!);

        transaction.set(docRef, storedScene);

        return { storedScene, storedElements: elements };
      }

      const prevStoredScene = snapshot.data() as FirebaseStoredScene;
      if (prevStoredScene.previousSceneObjectId) {
        obsoleteSceneObjectIds.add(prevStoredScene.previousSceneObjectId);
      }
      const prevStoredElements = getSyncableElements(
        restoreElements(
          await decryptElements(prevStoredScene, roomKey, roomId, ownerWindow),
          null,
        ),
      );
      const reconciledElements = getSyncableElements(
        reconcileElements(
          elements,
          prevStoredElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );

      const storedScene = await createFirebaseSceneDocument(
        reconciledElements,
        roomKey,
        roomId,
        ownerWindow,
      );
      uploadedSceneObjectIds.add(storedScene.sceneObjectId!);
      if (prevStoredScene.sceneObjectId) {
        storedScene.previousSceneObjectId = prevStoredScene.sceneObjectId;
      }

      transaction.update(docRef, {
        ...storedScene,
        // Remove the legacy in-document payload after the R2 write succeeds.
        ciphertext: deleteField(),
      });

      // The caller supplied an isolated scene clone. The successful
      // transaction result is therefore immutable from the caller's point of
      // view, and does not need to be downloaded and decoded from R2 again.
      return { storedScene, storedElements: reconciledElements };
    },
  );

  const currentSceneObjectId = storedScene.sceneObjectId;
  const sceneObjectIdsToDelete = new Set([
    ...obsoleteSceneObjectIds,
    ...uploadedSceneObjectIds,
  ]);
  if (currentSceneObjectId) {
    sceneObjectIdsToDelete.delete(currentSceneObjectId);
  }
  if (storedScene.previousSceneObjectId) {
    sceneObjectIdsToDelete.delete(storedScene.previousSceneObjectId);
  }
  Promise.all(
    [...sceneObjectIdsToDelete].map((sceneObjectId) =>
      deleteSceneFromStorage({
        roomId,
        roomKey,
        sceneObjectId,
        ownerWindow,
      }),
    ),
  ).catch((error) => console.warn("Could not clean up old scene data", error));

  FirebaseSceneVersionCache.set(socket, storedElements);

  return toBrandedType<RemoteExcalidrawElement[]>([...storedElements]);
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
  ownerWindow: Window & typeof globalThis,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const firestore = _getFirestore();
  const docRef = doc(firestore, "scenes", roomId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    return null;
  }
  const storedScene = docSnap.data() as FirebaseStoredScene;
  const elements = getSyncableElements(
    restoreElements(
      await decryptElements(storedScene, roomKey, roomId, ownerWindow),
      null,
      {
        deleteInvisibleElements: true,
      },
    ),
  );

  if (socket) {
    FirebaseSceneVersionCache.set(socket, elements);
  }

  return elements;
};

export const loadFilesFromStorage = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(getStorageFileUrl(`${prefix}/${id}`));
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
